import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StaticTreeDataProvider } from "react-complex-tree";
import { useMatch } from "react-router-dom";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import { CountTagItem, TagText } from "components/atoms/Tag";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import ContextMenu from "components/molecules/ContextMenu";
import {
    TreeItemArrow,
    TreeItemChildren,
    TreeItemContainer,
    TreeItemKnob,
    TreeItemMain,
    TreeItemTitle,
    allRefsTreeDataProvider,
} from "components/molecules/tree/Tree";
import TreeEnvironmentWrapper from "components/molecules/tree/TreeEnvironmentWrapper";
import FolderEditModal from "components/page/modal/FolderEditModal";

import {
    useFolderChangeOrder,
    useFolderCreate,
    useFolderDelete,
    useFolderMerge,
    useFolderReferenceCreate,
    useFolderUpdate,
    useFolders,
} from "hooks/queries/useBibliographies";

const RefTagWrap = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
`;

const TitleWrapper = styled.div`
    display: flex;
    align-items: center;
    overflow: hidden;
`;

const TextWrapper = styled.div`
    flex: 1;
    overflow: hidden;
`;

const ItemText = styled(Text)`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 1.4rem;
    font-weight: ${(props) => (props.$isSelected ? "500" : "400")};
    color: ${(props) => (props.$isSelected ? "var(--color-Black)" : "var(--color-SubBlack)")};
`;

const BlankBox = styled.div`
    width: 20px;
    height: 20px;
`;

const IconBox = styled.div`
    display: flex;
    align-items: center;
    margin: 0 8px 0 8px;
    width: 10px;
    height: 24px;
`;

const IconWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border-radius: 2px;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const RefTagBody = styled.div`
    overflow-y: auto;
`;

const LoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    margin-top: 30px;
`;

function ReferenceFilterFolderView(props) {
    // props:
    // viewState
    // onSelectItems
    // treeId
    const { treeId } = props;
    const [folders, setFolders] = useState({
        root: {
            index: "root",
            name: "root",
        },
    });
    const [viewState, setViewState] = useState({
        [treeId]: {
            selectedItems: [],
            expandedItems: sessionStorage.getItem(`${treeId}-expand-items`)
                ? JSON.parse(sessionStorage.getItem(`${treeId}-expand-items`))
                : [],
        },
    });

    const folderQuery = useFolders();
    const folderUpdate = useFolderUpdate();
    const folderCreate = useFolderCreate();
    const folderDelete = useFolderDelete();
    const folderChangeOrder = useFolderChangeOrder();
    const folderMerge = useFolderMerge();
    const { setToast } = useToast();

    const tree = useRef(null);

    const isReferenceDragEvent = (e) => {
        const dataTransfer = e.dataTransfer;
        if (!dataTransfer) {
            return false;
        }

        const dragTypes = Array.from(dataTransfer.types || []);
        if (dragTypes.includes("application/json")) {
            return true;
        }

        const data = dataTransfer.getData("application/json");
        if (!data) {
            return false;
        }

        try {
            const dragData = JSON.parse(data);
            return dragData.references && Array.isArray(dragData.references);
        } catch (err) {
            return false;
        }
    };

    // 각 부모의 자식 노드를 재귀적으로 정렬하는 함수
    const buildTreeWithOrdering = useCallback((items, parentId) => {
        const children = Object.values(items)
            .filter((item) => item.parent === parentId)
            .sort((a, b) => a.order - b.order);

        // 각 자식 노드에 대해 재귀적으로 정렬

        children.forEach((child) => {
            child.children = buildTreeWithOrdering(items, child.index).map((childItem) => childItem.index);
        });
        return children;
    }, []);

    useEffect(() => {
        const selectedItems = props.searchParams?.folder ? [props.searchParams.folder] : [];
        setViewState((prevState) => ({
            ...prevState,
            [treeId]: {
                ...prevState[treeId],
                selectedItems: selectedItems,
            },
        }));
    }, [props.searchParams, treeId]);

    const onCollapseItem = (item) => {
        const expandedItems = viewState[props.treeId].expandedItems.filter((id) => id !== item.index);
        setViewState((prevState) => ({
            ...prevState,
            [props.treeId]: {
                ...prevState[props.treeId],
                expandedItems: expandedItems,
            },
        }));
        sessionStorage.setItem(`${props.treeId}-expand-items`, JSON.stringify(expandedItems));
    };

    const onExpandItem = (item) => {
        const expandedItems = [...viewState[props.treeId].expandedItems, item.index];
        setViewState((prevState) => ({
            ...prevState,
            [props.treeId]: {
                ...prevState[props.treeId],
                expandedItems: expandedItems,
            },
        }));
        sessionStorage.setItem(`${props.treeId}-expand-items`, JSON.stringify(expandedItems));
    };

    useEffect(() => {
        if (folderQuery.data) {
            const items = {};
            // 아이템을 `index`를 키로 하여 맵에 저장
            folderQuery.data.forEach((folder) => {
                items[folder.id] = {
                    index: folder.id,
                    name: folder.name,
                    children: folder.children,
                    order: folder.order,
                    parent: folder.parent,
                    isFolder: folder.children.length > 0,
                    reference_count: folder.reference_count,
                };
            });

            // 재귀적으로 트리를 정렬
            const rootChildren = buildTreeWithOrdering(items, null).map((item) => item.index);

            // `root` 노드에 최상위 레벨의 자식 노드를 추가
            items["root"] = {
                index: "root",
                name: "root",
                children: rootChildren,
            };

            setFolders(items);
        }
    }, [buildTreeWithOrdering, folderQuery.data]);

    const dataProvider = useMemo(
        () =>
            new StaticTreeDataProvider(folders, (item, name) => ({
                ...item,
                name,
            })),
        [folders],
    );

    useEffect(() => {
        const allNodeIds = Object.keys(folders);
        dataProvider.onDidChangeTreeDataEmitter.emit(allNodeIds);
    }, [dataProvider.onDidChangeTreeDataEmitter, folders]);

    dataProvider.onChangeItemChildren = function (itemId, newChildren) {
        const item = this.data.items[itemId];
        item.children = newChildren;
        item.isFolder = newChildren.length > 0;
        this.onDidChangeTreeDataEmitter.emit([itemId]);
    };

    const removeItem = (itemIds = undefined) => {
        if (!itemIds) {
            itemIds = props.viewState[props.treeId].selectedItems;
        }

        const deleteItems = new Set(); // 중복을 제거하기 위해 Set 사용

        const addToDeleteItems = (itemId) => {
            if (!deleteItems.has(itemId)) {
                deleteItems.add(itemId);
                const children = folders[itemId].children || [];
                children.forEach(addToDeleteItems);
            }
        };

        itemIds.forEach(addToDeleteItems);

        Confirm("warn", "폴더 삭제", "폴더를 삭제하시겠습니까? 하위 폴더들도 함께 삭제됩니다.", "삭제", () => {
            folderDelete.mutate(
                {
                    ids: Array.from(deleteItems),
                },
                {
                    onSuccess: (data, variables, context) => {
                        setToast("폴더가 삭제되었습니다.", "info");
                        const selectedItem = viewState[props.treeId].selectedItems[0];
                        if (deleteItems.has(selectedItem)) {
                            props.onSelectItems([]);
                        }
                    },
                    onError: (error) => {
                        setToast("폴더 삭제에 실패했습니다. 잠시후 시도해주세요.", "error");
                    },
                },
            );
        });
    };

    const isPathReference = useMatch("/:groupId/references");
    const isFilterEmpty = !props.searchParams?.folder && !props.searchParams?.tags && isPathReference !== null;

    const [showContextMenu, setShowContextMenu] = useState({
        index: null,
        x: 0,
        y: 0,
    });
    const [showTooltip, setShowTooltip] = useState(false);

    //region: 참고문헌 드래그 앤 드롭
    const [referenceDragIndex, setReferenceDragIndex] = useState(null);
    const [referenceDragAll, setReferenceDragAll] = useState(false);

    const referenceFolderCreate = useFolderReferenceCreate();
    const handleReferenceDrop = (referenceIds, targetFolder) => {
        referenceFolderCreate.mutate(
            {
                folder_id: targetFolder ? targetFolder.index : null,
                reference_ids: referenceIds,
            },
            {
                onSuccess: () => {
                    setToast("선택한 폴더로 참고문헌이 이동되었습니다.", "info");
                },
                onError: (error) => {
                    Alert("warn", "폴더 연결 실패", "폴더 연결에 실패했습니다.");
                },
            },
        );
    };
    //endregion

    //region: 마크업 시작
    return (
        <>
            {
                // 로딩 이미지
                folderQuery.isLoading ? (
                    <LoadingWrap>
                        <Icon name={"load"} size={"40px"} />
                    </LoadingWrap>
                ) : (
                    <RefTagWrap>
                        <RefTagBody>
                            <TreeEnvironmentWrapper
                                treeId={"allOnlyTree"}
                                dataProvider={allRefsTreeDataProvider}
                                viewState={{
                                    allOnlyTree: {
                                        selectedItems: [],
                                        expandedItems: ["root"],
                                    },
                                }}
                                multiSelect={false}
                                canDropOnFolder={false}
                                canDropOnNonFolder={true}
                                renderItem={({ item, info, title, arrow, depth, context, children }) => (
                                    <TreeItemContainer
                                        depth={0}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            props.onSelectItems([]);
                                            tree?.current.unselectAllItems();
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setReferenceDragAll(true);
                                        }}
                                        onDragLeave={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setReferenceDragAll(false);
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setReferenceDragAll(false);

                                            // 드래그 소스에서 JSON 형식의 데이터를 가져옵니다.
                                            const data = e.dataTransfer.getData("application/json");
                                            if (data) {
                                                try {
                                                    const dragData = JSON.parse(data);
                                                    // 드래그 데이터에 references 속성이 있다면 참고문헌 드롭으로 판단
                                                    if (dragData.references && Array.isArray(dragData.references)) {
                                                        handleReferenceDrop(dragData.references, null);
                                                        return;
                                                    }
                                                } catch (err) {
                                                    console.error("drop data error", err);
                                                }
                                            }
                                        }}
                                    >
                                        <TreeItemMain isDraggingOver={referenceDragAll} isSelected={isFilterEmpty}>
                                            <TreeItemTitle>
                                                <IconBox>
                                                    {isFilterEmpty ? (
                                                        <Icon
                                                            name={"check"}
                                                            size={"10px"}
                                                            color={"var(--color-DarkKey)"}
                                                        />
                                                    ) : (
                                                        <Icon name={"folder"} size={"10px"} color={"#9FA8AB"} />
                                                    )}
                                                </IconBox>
                                                <ItemText size={"regular"} $isSelected={isFilterEmpty}>
                                                    모든 참고문헌
                                                </ItemText>
                                            </TreeItemTitle>
                                        </TreeItemMain>
                                    </TreeItemContainer>
                                )}
                            ></TreeEnvironmentWrapper>
                            <TreeEnvironmentWrapper
                                treeId={props.treeId}
                                ref={tree}
                                dataProvider={dataProvider}
                                viewState={viewState}
                                multiSelect={false}
                                canDropOnFolder={true}
                                canDropOnNonFolder={true}
                                onSelectItems={(selectedItems) => {
                                    props.onSelectItems(selectedItems);
                                }}
                                onDrop={async (updatedNodes) => {
                                    try {
                                        await folderChangeOrder.mutateAsync(updatedNodes);
                                        setToast("폴더 순서가 변경되었습니다.", "info");
                                    } catch (error) {
                                        console.log(error);
                                        if (error?.response?.status === 400) {
                                            if (error?.response?.data?.errors) {
                                                Confirm(
                                                    "warn",
                                                    "알림",
                                                    "이미 존재하는 폴더명입니다. 병합하시겠습니까?",
                                                    "병합",
                                                    () => {
                                                        folderMerge.mutate(error.response.data.instance, {
                                                            onSuccess: (data, variables, context) => {
                                                                setToast("폴더가 병합되었습니다.", "info");
                                                            },
                                                            onError: (error) => {
                                                                console.log(error);
                                                                Alert("error", "폴더 병합에 실패했습니다");
                                                            },
                                                        });
                                                    },
                                                    () => {
                                                        // 변경 전 데이터를 불러와 업데이트
                                                        const items = {};
                                                        // 아이템을 `index`를 키로 하여 맵에 저장
                                                        folderQuery.data.forEach((folder) => {
                                                            items[folder.id] = {
                                                                index: folder.id,
                                                                name: folder.name,
                                                                children: folder.children,
                                                                order: folder.order,
                                                                parent: folder.parent,
                                                                isFolder: folder.children.length > 0,
                                                                reference_count: folder.reference_count,
                                                            };
                                                        });

                                                        // 재귀적으로 트리를 정렬
                                                        const rootChildren = buildTreeWithOrdering(items, null).map(
                                                            (item) => item.index,
                                                        );

                                                        // `root` 노드에 최상위 레벨의 자식 노드를 추가
                                                        items["root"] = {
                                                            index: "root",
                                                            name: "root",
                                                            children: rootChildren,
                                                        };
                                                        setFolders(items);
                                                    },
                                                );
                                            }
                                        }
                                    }
                                }}
                                onCollapseItem={onCollapseItem}
                                onExpandItem={onExpandItem}
                                renderItem={({ item, info, title, arrow, depth, context, children }) => (
                                    <TreeItemContainer
                                        {...context.itemContainerWithChildrenProps}
                                        depth={Number(depth)}
                                        onMouseLeave={() =>
                                            setShowContextMenu({
                                                index: null,
                                                x: 0,
                                                y: 0,
                                            })
                                        }
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            setReferenceDragIndex(null);

                                            // 드래그 소스에서 JSON 형식의 데이터를 가져옵니다.
                                            const data = e.dataTransfer.getData("application/json");
                                            if (data) {
                                                try {
                                                    const dragData = JSON.parse(data);
                                                    // 드래그 데이터에 references 속성이 있다면 참고문헌 드롭으로 판단
                                                    if (dragData.references && Array.isArray(dragData.references)) {
                                                        e.stopPropagation();
                                                        handleReferenceDrop(dragData.references, item);
                                                        return;
                                                    }
                                                } catch (err) {
                                                    console.error("drop data error", err);
                                                }
                                            }
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();

                                            // 외부 참고문헌 드래그에만 전파를 차단하고,
                                            // 폴더 자체 드래그는 react-complex-tree가 위치를 계산하도록 그대로 버블링시킨다.
                                            if (isReferenceDragEvent(e)) {
                                                setReferenceDragIndex(item.index);
                                                e.stopPropagation();
                                            } else if (referenceDragIndex !== null) {
                                                setReferenceDragIndex(null);
                                            }
                                        }}
                                        onDragLeave={(e) => {
                                            e.preventDefault();
                                            if (isReferenceDragEvent(e)) {
                                                setReferenceDragIndex(null);
                                            }
                                        }}
                                    >
                                        <TreeItemMain
                                            {...context.itemContainerWithoutChildrenProps}
                                            {...context.interactiveElementProps}
                                            isDraggingOver={context.isDraggingOver || referenceDragIndex === item.index}
                                            isSelected={context.isSelected}
                                        >
                                            {item.isFolder ? (
                                                <TreeItemArrow
                                                    {...context.arrowProps}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (context.isExpanded) {
                                                            tree.current?.collapseItem(item.index);
                                                        } else {
                                                            tree.current?.expandItem(item.index);
                                                        }
                                                    }}
                                                >
                                                    <IconWrap>
                                                        <Icon
                                                            name={
                                                                context.isExpanded
                                                                    ? "folderDownward"
                                                                    : "folderRightward"
                                                            }
                                                            size={"14px"}
                                                        />
                                                    </IconWrap>
                                                </TreeItemArrow>
                                            ) : (
                                                <BlankBox />
                                            )}

                                            <TreeItemTitle>
                                                <IconBox>
                                                    {context.isSelected ? (
                                                        <Icon
                                                            name={"check"}
                                                            size={"10px"}
                                                            color={"var(--color-DarkKey)"}
                                                        />
                                                    ) : (
                                                        <Icon name={"folder"} size={"10px"} color={"#9FA8AB"} />
                                                    )}
                                                </IconBox>
                                                <TitleWrapper>
                                                    <TextWrapper>
                                                        <ItemText $isSelected={context.isSelected}>{title}</ItemText>
                                                    </TextWrapper>
                                                </TitleWrapper>
                                                <CountTagItem>
                                                    <TagText>{item.reference_count}</TagText>
                                                </CountTagItem>
                                            </TreeItemTitle>
                                            <TreeItemKnob
                                                className={`tree-knob-${item.index}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setShowContextMenu(
                                                        showContextMenu.index === item.index
                                                            ? {
                                                                  index: null,
                                                                  x: 0,
                                                                  y: 0,
                                                              }
                                                            : {
                                                                  index: item.index,
                                                                  x: e.currentTarget.getBoundingClientRect().left,
                                                                  y: e.currentTarget.getBoundingClientRect().bottom,
                                                              },
                                                    );
                                                }}
                                            >
                                                {showTooltip ? (
                                                    <Tooltip message={"더보기"}>
                                                        <IconWrap
                                                            onMouseEnter={() => setShowTooltip(true)}
                                                            onMouseLeave={() => setShowTooltip(false)}
                                                        >
                                                            <Icon name={"submenu"} />
                                                        </IconWrap>
                                                    </Tooltip>
                                                ) : (
                                                    <IconWrap
                                                        onMouseEnter={() => setShowTooltip(true)}
                                                        onMouseLeave={() => setShowTooltip(false)}
                                                    >
                                                        <Icon name={"submenu"} />
                                                    </IconWrap>
                                                )}
                                                {showContextMenu.index === item.index && (
                                                    <ContextMenu
                                                        show={true}
                                                        position={{ x: showContextMenu.x, y: showContextMenu.y - 20 }}
                                                        minWidth={"70px"}
                                                        items={[
                                                            {
                                                                name: "새 폴더",
                                                                action: () => {
                                                                    props.setShowFolderAddModal({
                                                                        type: "create",
                                                                        parentIndex: item.index,
                                                                    });
                                                                },
                                                            },
                                                            {
                                                                name: "이름 변경",
                                                                action: () => {
                                                                    props.setShowFolderAddModal({
                                                                        type: "update",
                                                                        item: item,
                                                                    });
                                                                },
                                                            },
                                                            {
                                                                name: "삭제",
                                                                action: () => {
                                                                    removeItem([item.index]);
                                                                },
                                                            },
                                                        ]}
                                                        onClose={() =>
                                                            setShowContextMenu({
                                                                index: null,
                                                                x: 0,
                                                                y: 0,
                                                            })
                                                        }
                                                        triggerClassName={`tree-knob-${item.index}`}
                                                    />
                                                )}
                                            </TreeItemKnob>
                                        </TreeItemMain>
                                        <TreeItemChildren>{children}</TreeItemChildren>
                                    </TreeItemContainer>
                                )}
                            />
                        </RefTagBody>
                    </RefTagWrap>
                )
            }
            {props.showFolderAddModal.type && (
                <FolderEditModal
                    exitModal={(e) =>
                        props.setShowFolderAddModal({
                            type: "",
                        })
                    }
                    onSelectItems={props.onSelectItems}
                    expandItem={(item) => {
                        tree.current?.expandItem(item);
                    }}
                    mutation={props.showFolderAddModal.type === "create" ? folderCreate : folderUpdate}
                    context={props.showFolderAddModal}
                />
            )}
        </>
    );
}

export default ReferenceFilterFolderView;
